<template>
  <div class="ordenes-compras">

    <!-- Cabecera -->
    <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
      <h5 class="ordenes-titulo mb-0">
        <i class="bi bi-bag-check me-2"></i>Compras Registradas
      </h5>
      <router-link to="/compras/registrar" class="btn btn-sm btn-primary">
        <i class="bi bi-plus-lg me-1"></i>Nueva Compra
      </router-link>
    </div>

    <!-- Filtros -->
    <div class="ordenes-panel mb-3">
      <div class="ordenes-panel__cuerpo">
        <div class="row g-2 align-items-end">
          <div class="col-sm-4 col-md-3">
            <label class="form-label form-label-sm mb-1">Desde</label>
            <input type="date" class="form-control form-control-sm" v-model="filtros.desde" @change="cargar" />
          </div>
          <div class="col-sm-4 col-md-3">
            <label class="form-label form-label-sm mb-1">Hasta</label>
            <input type="date" class="form-control form-control-sm" v-model="filtros.hasta" @change="cargar" />
          </div>
          <div class="col-sm-4 col-md-4">
            <label class="form-label form-label-sm mb-1">Proveedor</label>
            <input type="text" class="form-control form-control-sm" v-model="filtros.proveedor" placeholder="Nombre o RUC" @keyup.enter="cargar" />
          </div>
          <div class="col-auto d-flex gap-1">
            <button class="btn btn-sm btn-outline-secondary" @click="cargar">
              <i class="bi bi-search me-1"></i>Buscar
            </button>
            <button class="btn btn-sm btn-link ms-1" style="color:var(--text-muted)" @click="limpiarFiltros">Limpiar</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Alerta -->
    <div v-if="alerta.mensaje" class="ordenes-alerta d-flex align-items-center gap-2 mb-3" :class="alerta.tipo === 'error' ? 'ordenes-alerta--error' : 'ordenes-alerta--exito'">
      <i :class="alerta.tipo === 'error' ? 'bi bi-x-circle-fill' : 'bi bi-check-circle-fill'"></i>
      <span>{{ alerta.mensaje }}</span>
    </div>

    <!-- Tabla -->
    <div class="ordenes-panel">
      <!-- Estado: cargando -->
      <div v-if="cargando" class="ordenes-estado-vacio">
        <span class="spinner-border spinner-border-sm me-2"></span>Cargando compras…
      </div>

      <!-- Estado: vacío -->
      <div v-else-if="compras.length === 0" class="ordenes-estado-vacio">
        <i class="bi bi-inbox fs-2 d-block mb-2"></i>No se encontraron compras con los filtros aplicados.
      </div>

      <!-- Tabla principal -->
      <div v-else class="table-responsive">
        <table class="ordenes-tabla">
          <thead>
            <tr>
              <th style="width:36px"></th>
              <th>Nº Factura</th>
              <th>Proveedor</th>
              <th>Fecha</th>
              <th class="text-end">Subtotal</th>
              <th class="text-end">IVA</th>
              <th class="text-end">Total</th>
              <th>Origen</th>
              <th class="text-center">Productos</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="c in compras" :key="c.id">
              <!-- Fila principal -->
              <tr
                class="ordenes-fila"
                :class="{ 'ordenes-fila--activa': expandido === c.id }"
                @click="toggleDetalle(c.id)"
              >
                <td class="text-center">
                  <i :class="expandido === c.id ? 'bi bi-chevron-up' : 'bi bi-chevron-down'" class="ordenes-fila__chevron"></i>
                </td>
                <td class="font-monospace ordenes-td--mono fw-semibold">{{ c.numero_factura }}</td>
                <td>
                  <div class="fw-semibold text-truncate ordenes-proveedor-nombre" :title="c.proveedor_nombre">{{ c.proveedor_nombre }}</div>
                  <div class="font-monospace ordenes-proveedor-ruc">{{ c.proveedor_ruc }}</div>
                </td>
                <td class="ordenes-td--sm">{{ formatFecha(c.fecha_emision) }}</td>
                <td class="text-end ordenes-td--sm">${{ fmt(c.subtotal) }}</td>
                <td class="text-end ordenes-td--sm">${{ fmt(c.iva) }}</td>
                <td class="text-end fw-bold ordenes-td--total">${{ fmt(c.total) }}</td>
                <td>
                  <span class="badge" :class="c.tipo_ingreso === 'xml_sri' ? 'bg-success' : 'bg-secondary'">
                    <i :class="c.tipo_ingreso === 'xml_sri' ? 'bi bi-file-earmark-code' : 'bi bi-pencil'" class="me-1"></i>
                    {{ c.tipo_ingreso === 'xml_sri' ? 'XML' : 'Manual' }}
                  </span>
                </td>
                <td class="text-center">
                  <span v-if="detalles[c.id]" class="ordenes-badge-vinc">
                    {{ detalles[c.id].filter(d => d.producto_id).length }}/{{ detalles[c.id].length }}
                    <span class="ordenes-badge-vinc__label">vinc.</span>
                  </span>
                  <span v-else-if="cargandoDetalle === c.id" class="spinner-border spinner-border-sm" style="color:var(--text-muted)"></span>
                </td>
              </tr>

              <!-- Fila de detalle expandida -->
              <tr v-if="expandido === c.id" class="ordenes-detalle-fila">
                <td colspan="9" class="p-0">
                  <div class="ordenes-detalle">
                    <div v-if="cargandoDetalle === c.id" class="ordenes-estado-vacio py-3">
                      <span class="spinner-border spinner-border-sm me-2"></span>Cargando detalle…
                    </div>
                    <div v-else-if="detalles[c.id]">
                      <div class="d-flex align-items-center justify-content-between mb-2">
                        <span class="ordenes-detalle__titulo">
                          <i class="bi bi-list-ul me-1"></i>Productos de esta factura
                        </span>
                        <span class="ordenes-detalle__contador">
                          <span style="color:var(--accent-success)">{{ detalles[c.id].filter(d => d.producto_id).length }} vinculados</span>
                          · {{ detalles[c.id].filter(d => !d.producto_id).length }} sin vincular
                        </span>
                      </div>
                      <div class="table-responsive">
                        <table class="ordenes-subtabla">
                          <thead>
                            <tr>
                              <th>Código</th>
                              <th>Descripción</th>
                              <th class="text-end" style="width:60px">Cant.</th>
                              <th class="text-end" style="width:90px">Costo u.</th>
                              <th class="text-end" style="width:90px">Total</th>
                              <th style="width:200px">Producto en inventario</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="d in detalles[c.id]" :key="d.id" class="ordenes-subtabla__fila" :class="{ 'ordenes-subtabla__fila--vinculada': d.producto_id }">
                              <td class="font-monospace" style="font-size:0.75rem;color:var(--text-muted)">{{ d.codigo_proveedor || '—' }}</td>
                              <td style="color:var(--text-primary)">{{ d.descripcion }}</td>
                              <td class="text-end" style="color:var(--text-secondary)">{{ d.cantidad }}</td>
                              <td class="text-end" style="color:var(--text-secondary)">${{ fmt(d.costo_unitario) }}</td>
                              <td class="text-end fw-semibold" style="color:var(--text-primary)">${{ fmt(d.total) }}</td>
                              <td>
                                <span v-if="d.producto_id" class="d-flex align-items-center gap-1">
                                  <i class="bi bi-check-circle-fill" style="color:var(--accent-success);font-size:.8rem"></i>
                                  <span class="fw-semibold" style="font-size:.82rem;color:var(--text-primary)">{{ d.nombre_producto || '—' }}</span>
                                  <span v-if="d.codigo_principal" class="font-monospace" style="font-size:0.7rem;color:var(--text-muted)">{{ d.codigo_principal }}</span>
                                </span>
                                <span v-else class="d-flex align-items-center gap-1" style="color:var(--text-muted);font-size:.82rem">
                                  <i class="bi bi-dash-circle" style="font-size:.8rem"></i>Sin vincular
                                </span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- Paginación -->
      <div v-if="!cargando && total > limit" class="ordenes-paginacion">
        <span>Mostrando {{ offset + 1 }}–{{ Math.min(offset + compras.length, total) }} de {{ total }}</span>
        <div class="d-flex gap-1">
          <button class="btn btn-sm btn-outline-secondary" :disabled="offset === 0" @click="paginar(-1)">
            <i class="bi bi-chevron-left"></i>
          </button>
          <button class="btn btn-sm btn-outline-secondary" :disabled="offset + limit >= total" @click="paginar(1)">
            <i class="bi bi-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'ClientComprasOrdenes',

  data() {
    return {
      compras:         [],
      detalles:        {}, // { compra_id: [...] }
      expandido:       null,
      cargandoDetalle: null,
      cargando:        false,
      total:           0,
      limit:           50,
      offset:          0,
      alerta:          { mensaje: '', tipo: 'error' },
      filtros: {
        desde:     '',
        hasta:     '',
        proveedor: '',
      },
    }
  },

  computed: {
    fmt() {
      return (v) => Number(v || 0).toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
  },

  created() {
    const idParam = this.$route?.params?.id
    this.cargar().then(() => {
      if (idParam) this.toggleDetalle(parseInt(idParam))
    })
  },

  methods: {
    formatFecha(f) {
      if (!f) return '—'
      const [y, m, d] = f.split('-')
      return `${d}/${m}/${y}`
    },

    async cargar() {
      this.cargando = true
      this.alerta   = { mensaje: '', tipo: 'error' }
      try {
        const params = { limit: this.limit, offset: this.offset }
        if (this.filtros.desde) params.fecha_desde = this.filtros.desde
        if (this.filtros.hasta) params.fecha_hasta  = this.filtros.hasta
        const { data } = await api.get('/compras', { params })
        this.compras = data.data || []
        this.total   = data.total || this.compras.length
      } catch {
        this.alerta = { mensaje: 'No se pudo cargar el listado de compras.', tipo: 'error' }
      } finally {
        this.cargando = false
      }
    },

    async toggleDetalle(id) {
      if (this.expandido === id) {
        this.expandido = null
        return
      }
      this.expandido = id
      if (this.detalles[id]) return
      this.cargandoDetalle = id
      try {
        const { data } = await api.get(`/compras/${id}`)
        this.detalles = { ...this.detalles, [id]: data.data?.detalles || [] }
      } catch {
        this.detalles = { ...this.detalles, [id]: [] }
      } finally {
        this.cargandoDetalle = null
      }
    },

    limpiarFiltros() {
      this.filtros = { desde: '', hasta: '', proveedor: '' }
      this.offset  = 0
      this.cargar()
    },

    paginar(dir) {
      this.offset = Math.max(0, this.offset + dir * this.limit)
      this.cargar()
    },
  },
}
</script>

<style scoped>
/* ── Contenedor ── */
.ordenes-compras {
  padding: 1.25rem;
}

.ordenes-titulo {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--primary-color);
}

/* ── Panel / Tarjeta ── */
.ordenes-panel {
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: 10px;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

.ordenes-panel__cuerpo {
  padding: 0.75rem 1rem;
}

/* ── Alerta ── */
.ordenes-alerta {
  padding: 0.5rem 0.85rem;
  border-radius: 8px;
  font-size: 0.875rem;
  border: 1px solid;
}

.ordenes-alerta--error {
  background: var(--accent-danger-light);
  border-color: var(--accent-danger);
  color: var(--accent-danger);
}

.ordenes-alerta--exito {
  background: var(--accent-success-light);
  border-color: var(--accent-success);
  color: var(--accent-success);
}

/* ── Estado vacío / cargando ── */
.ordenes-estado-vacio {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--text-muted);
  font-size: 0.9rem;
}

/* ── Tabla principal ── */
.ordenes-tabla {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.ordenes-tabla thead tr {
  background: var(--secondary-bg-soft);
  border-bottom: 2px solid var(--border-main);
}

.ordenes-tabla thead th {
  padding: 0.6rem 0.75rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

/* ── Fila principal ── */
.ordenes-fila {
  border-bottom: 1px solid var(--border-main);
  cursor: pointer;
  transition: background 0.1s;
}

.ordenes-fila td {
  padding: 0.55rem 0.75rem;
  vertical-align: middle;
  color: var(--text-primary);
}

.ordenes-fila:hover {
  background: var(--table-hover);
}

.ordenes-fila--activa {
  background: var(--accent-info-light) !important;
  border-left: 3px solid var(--primary-color);
}

.ordenes-fila__chevron {
  font-size: 0.75rem;
  color: var(--text-muted);
}

/* ── Celdas especiales ── */
.ordenes-td--mono  { font-size: 0.82rem; }
.ordenes-td--sm    { font-size: 0.82rem; color: var(--text-secondary); }
.ordenes-td--total { font-size: 0.9rem; color: var(--text-primary); }

.ordenes-proveedor-nombre {
  font-size: 0.85rem;
  max-width: 200px;
  color: var(--text-primary);
}

.ordenes-proveedor-ruc {
  font-size: 0.7rem;
  color: var(--text-muted);
}

/* ── Badge vinculados ── */
.ordenes-badge-vinc {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.2rem 0.5rem;
  border-radius: 20px;
  background: var(--secondary-bg-soft);
  border: 1px solid var(--border-main);
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-primary);
}

.ordenes-badge-vinc__label {
  font-size: 0.65rem;
  color: var(--text-muted);
}

/* ── Fila de detalle expandido ── */
.ordenes-detalle-fila {
  border-bottom: 1px solid var(--border-main);
}

.ordenes-detalle {
  background: var(--secondary-bg-soft);
  border-top: 1px solid var(--border-main);
  padding: 1rem 1.25rem;
}

.ordenes-detalle__titulo {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.ordenes-detalle__contador {
  font-size: 0.8rem;
  color: var(--text-muted);
}

/* ── Sub-tabla de productos ── */
.ordenes-subtabla {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: 6px;
  overflow: hidden;
}

.ordenes-subtabla thead tr {
  background: var(--bg-tertiary);
  border-bottom: 1px solid var(--border-main);
}

.ordenes-subtabla thead th {
  padding: 0.45rem 0.65rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.ordenes-subtabla__fila td {
  padding: 0.4rem 0.65rem;
  border-bottom: 1px solid var(--border-main);
  vertical-align: middle;
}

.ordenes-subtabla__fila:last-child td {
  border-bottom: none;
}

.ordenes-subtabla__fila:hover {
  background: var(--table-hover);
}

.ordenes-subtabla__fila--vinculada td:last-child {
  background: rgba(16, 185, 129, 0.04);
}

/* ── Paginación ── */
.ordenes-paginacion {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.85rem;
  border-top: 1px solid var(--border-main);
  font-size: 0.82rem;
  color: var(--text-muted);
}
</style>

